import type { DungeonId, Profile, Vec } from './types.js';

export const MAX_DEPTH = 12;
/** Ordinary doors run one to five. The Elder Convergence remains the top of the ladder. */
export const MAX_DOOR_DEPTH = 5;
export const depthCap = (template: string) => (template === 'eclipse' ? MAX_DEPTH : MAX_DOOR_DEPTH);
export const ELDER_KINDS = ['tideelder', 'cinderelder', 'nullelder'] as const;
export const MODIFIERS = {
  still: {
    name: 'Still waters',
    description: 'The standard expedition.',
    hp: 1,
    damage: 1,
    speed: 1,
    rate: 1,
  },
  iron: {
    name: 'Iron hearts',
    description: 'Enemies have 20% more health.',
    hp: 1.2,
    damage: 1,
    speed: 1,
    rate: 1,
  },
  swift: {
    name: 'Swift current',
    description: 'Hostile projectiles travel 8% faster.',
    hp: 1,
    damage: 1,
    speed: 1.08,
    rate: 1,
  },
  fervor: {
    name: 'Elder fervor',
    description: 'Enemies attack 12% more often.',
    hp: 1,
    damage: 1,
    speed: 1,
    rate: 0.88,
  },
  brittle: {
    name: 'Brittle hearts',
    description: 'Enemies have 15% less health and hit 15% harder.',
    hp: 0.85,
    damage: 1.15,
    speed: 1,
    rate: 1,
  },
  patient: {
    name: 'Patient storm',
    description: 'Shots travel 12% slower and come 8% more often.',
    hp: 1,
    damage: 1,
    speed: 0.88,
    rate: 0.92,
  },
  heavy: {
    name: 'Heavy air',
    description: 'Everything hits 20% harder and attacks 10% less often.',
    hp: 1,
    damage: 1.2,
    speed: 1,
    rate: 1.1,
  },
  relentless: {
    name: 'Relentless',
    description: 'Enemies attack 20% more often and have 10% less health.',
    hp: 0.9,
    damage: 1,
    speed: 1,
    rate: 0.8,
  },
  gale: {
    name: 'Gale',
    description: 'Hostile projectiles travel 18% faster.',
    hp: 1,
    damage: 1,
    speed: 1.18,
    rate: 1,
  },
  stonebound: {
    name: 'Stonebound',
    description: 'Enemies have 35% more health and attack 12% less often.',
    hp: 1.35,
    damage: 1,
    speed: 1,
    rate: 1.12,
  },
  keen: {
    name: 'Keen edge',
    description: 'Enemies hit 25% harder but have 12% less health.',
    hp: 0.88,
    damage: 1.25,
    speed: 1,
    rate: 1,
  },
  drifting: {
    name: 'Drifting ash',
    description: 'Shots are 10% slower; enemies carry 15% more health.',
    hp: 1.15,
    damage: 1,
    speed: 0.9,
    rate: 1,
  },
} as const;
/** The rotation a door draws from when it is opened past depth one. */
export const DEPTH_MODIFIERS = [
  'iron',
  'swift',
  'fervor',
  'brittle',
  'patient',
  'heavy',
  'relentless',
  'gale',
  'stonebound',
  'keen',
  'drifting',
] as const;
/**
 * The realm's weather this week. Drawn from the same table, applied to everything in the
 * wilds, and announced at the Hearth: the reason to log in this week did not exist last week.
 */
export const SEASON_LENGTH = 7 * 24 * 60 * 60 * 1000;
export const seasonNumber = (at = Date.now()) => Math.floor(at / SEASON_LENGTH);
export function seasonModifier(at = Date.now()): Modifier {
  return DEPTH_MODIFIERS[seasonNumber(at) % DEPTH_MODIFIERS.length];
}
export type Modifier = keyof typeof MODIFIERS;
export function legacyOf(p: Profile) {
  return (
    p.legacy ?? {
      shards: 0,
      highestDepth: 0,
      selectedDepth: 1,
      clears: 0,
      bossKills: {},
      relics: [],
      bestTimes: {},
      bestDepths: {},
    }
  );
}
export function ensureLegacy(p: Profile) {
  const legacy = (p.legacy ??= legacyOf(p));
  // Migration, in place and without a schema change: best times used to be keyed by the bare
  // Elder depth, and depth credit lived in `highestDepth` alone. Both are per template now.
  legacy.bestDepths ??= {};
  if (legacy.highestDepth && !legacy.bestDepths.eclipse)
    legacy.bestDepths.eclipse = legacy.highestDepth;
  for (const [key, value] of Object.entries(legacy.bestTimes))
    if (/^\d+$/.test(key) && legacy.bestTimes[`eclipse:${key}`] === undefined)
      legacy.bestTimes[`eclipse:${key}`] = value;
  return legacy;
}
export function depthScaling(depth: number) {
  const d = Math.max(1, Math.min(MAX_DEPTH, depth)) - 1;
  return { hp: 1 + d * 0.11, damage: 1 + d * 0.035, speed: 1 + d * 0.012 };
}
export interface Encounter {
  name: string;
  altar: Vec;
  enemies: { kind: string; count: number; x: number; z: number; spread: number }[];
}
export const EXPEDITIONS: Record<DungeonId, Encounter[]> = {
  hollow: [
    {
      name: 'The flooded stacks',
      altar: { x: 0, z: 17 },
      enemies: [{ kind: 'wisp', count: 5, x: 0, z: 6, spread: 9 }],
    },
    {
      name: 'The forbidden index',
      altar: { x: 0, z: 3 },
      enemies: [
        { kind: 'wisp', count: 4, x: -6, z: -7, spread: 5 },
        { kind: 'thornling', count: 3, x: 7, z: -8, spread: 4 },
      ],
    },
    {
      name: 'The keeper of every name',
      altar: { x: 0, z: -8 },
      enemies: [{ kind: 'archivist', count: 1, x: 0, z: -20, spread: 0 }],
    },
  ],
  crucible: [
    {
      name: 'The cooling channels',
      altar: { x: 0, z: 17 },
      enemies: [{ kind: 'scarab', count: 6, x: 0, z: 6, spread: 9 }],
    },
    {
      name: 'The furnace choir',
      altar: { x: 0, z: 3 },
      enemies: [
        { kind: 'watcher', count: 4, x: 0, z: -6, spread: 8 },
        { kind: 'scarab', count: 3, x: 0, z: -10, spread: 5 },
      ],
    },
    {
      name: 'The first fire',
      altar: { x: 0, z: -8 },
      enemies: [{ kind: 'forgemother', count: 1, x: 0, z: -20, spread: 0 }],
    },
  ],
  eclipse: [
    {
      name: 'The oath at the threshold',
      altar: { x: 0, z: 17 },
      enemies: [{ kind: 'watcher', count: 6, x: 0, z: 5, spread: 10 }],
    },
    {
      name: 'A sea without a shore',
      altar: { x: 0, z: 1 },
      enemies: [{ kind: 'tideelder', count: 1, x: 0, z: -12, spread: 0 }],
    },
    {
      name: 'The last constellation',
      altar: { x: 0, z: 1 },
      enemies: [
        { kind: 'watcher', count: 4, x: -8, z: -10, spread: 5 },
        { kind: 'scarab', count: 4, x: 8, z: -10, spread: 5 },
      ],
    },
    {
      name: 'A sun that will not die',
      altar: { x: 0, z: 1 },
      enemies: [{ kind: 'cinderelder', count: 1, x: 0, z: -12, spread: 0 }],
    },
    {
      name: 'The silence after stars',
      altar: { x: 0, z: 1 },
      enemies: [{ kind: 'nullelder', count: 1, x: 0, z: -12, spread: 0 }],
    },
  ],
};
export const masteryTitle = (depth: number) =>
  depth >= 12
    ? 'Starbreaker'
    : depth >= 8
      ? 'Elderbane'
      : depth >= 4
        ? 'Dawnkeeper'
        : depth >= 1
          ? 'Convergence walker'
          : 'Ember bearer';
export const relicCost = (kind: string) =>
  ELDER_KINDS.includes(kind as (typeof ELDER_KINDS)[number]) ? 30 : 18;
